/**
 * Compare / Vergleichsliste - Varianten Selector
 *
 * Filename: 420_compare_variant_selection.js
 *
 * @category  Applicas
 * @package   Slider
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2022 Fahrrad XXL
 */


export default class FxxlCompareProductVariantSelector {

    constructor(container, product) {
        // Classnames & Idnames
        this.variantselectIdPrefix = 'variant-select';
        this.itemHideClass = 'fxxl-artikel-comparison__item--hide';
        this.elementHideClass = 'fxxl-hidden';

        // Variables
        this.container = container;
        this.product = product;
        this.productId = product.productId;
        this.variantselect = null;
        this.selectedValue = null;
        this.selectedOptionIndex = null;
        this.preselectedColor = '';
        this.preselectedColorValue = null;
        this.productOsz = null;
        this.productLowestPriceVariantId = '';

        // Init
        this.variantselect = this.container.querySelector(`#${this.variantselectIdPrefix}-${this.productId}`) || null;
        if (this.variantselect === null) return;

        this.initSetSelectedValues();
        this.initEvents();
        this.initShowFirstVisibleItems();
    }

    initSetSelectedValues() {
        this.preselectedColor               = this.variantselect.dataset.preselectedColor || '';
        this.productOsz                     = (this.variantselect.dataset.productOsz === 'true');
        this.productLowestPriceVariantId    = this.variantselect.dataset.productLowestPriceVariantId || '';
        this.selectedValue                  = this.variantselect.value || null;
        this.selectedOptionIndex            = this.variantselect.selectedIndex;

        // Set preselectedColorValue ( IF: Color was selected before )
        if (this.preselectedColor) {
            const selectedColorOption = Array.from(this.variantselect).find(item => {
                return item.dataset.color === this.preselectedColor
            });
            this.preselectedColorValue = (selectedColorOption) ? selectedColorOption.value : null;
        }

        // Set selectedValue
        if (this.selectedValue === null) {
            if (this.productOsz) {
                this.selectedValue = this.preselectedColorValue;
                this.variantselect.value = this.preselectedColorValue;

                const selectedVariantId = this.selectedValue.split('-')[1];
                this.updateProductByVariantId(selectedVariantId);
            } else {
                if (this.variantselect.options.length > 1) {
                    this.selectedValue = this.variantselect.options[1].value || null;
                }
            }
        }
    }

    initEvents() {
        this.variantselect.addEventListener('change', (e) => {
            const selectedIndex = e.target.selectedIndex;
            const selectedValue = e.target.value;
            const selectedVariantId = selectedValue.split('-')[1];

            if (!selectedVariantId) return;

            this.hideAllItems();
            this.showItemsByVariantId(selectedVariantId);
            this.resetAllVariantLinks();
            this.setSelectedVariantSelectOptionByIndex(selectedIndex);
            this.updateProductByVariantId(selectedVariantId);
        });
    }

    initShowFirstVisibleItems() {
        const selectedVariantId = this.selectedValue.split('-')[1];

        this.hideAllItems();
        this.showItemsByVariantId(selectedVariantId);

        // IF: No Variant was selected
        if (!this.hasVariantBeenSelected()) {
            // Show Main Product Articlenumber
            this.hideAllOfItemType('articlenumberItems');
            this.showTypeItemByVariantId('articlenumberItem', this.productId);

            // Show Price "Ab" Element
            this.showAllPriceFromElements();

            // Show Lowest Price
            if (this.productLowestPriceVariantId !== '') {
                const lowestPriceVariantId = this.productLowestPriceVariantId.split('-')[1];
                this.hideAllOfItemType('priceItems');
                this.showTypeItemByVariantId('priceItem', lowestPriceVariantId);
            }

            // Show a Colorvariant if one was selected
            if (this.preselectedColorValue !== null) {
                const selectedColorVariantId = this.preselectedColorValue.split('-')[1];

                this.hideAllOfItemType('modelItems');
                this.showTypeItemByVariantId('modelItem', selectedColorVariantId);

                // Temporarily remove varid from Link ( IF: No Variant, but a color was selected )
                this.setVariantLinkToProductLink(selectedColorVariantId);
            } else {
                // Temporarily remove varid from Link ( IF: Simply no Variant was selected )
                this.setVariantLinkToProductLink(selectedVariantId);
            }
        }
    }

    hideAllItems() {
        this.product['items'].forEach(item => item.classList.add(this.itemHideClass));
        this.hideAllPriceFromElements();
    }

    hideAllOfItemType(type) {
        if (this.product?.[type]) {
            this.product[type].forEach(item => item.classList.add(this.itemHideClass));
        }
    }

    hideAllPriceFromElements() {
        this.product?.['priceFromElements']?.forEach(item => item.classList.add(this.elementHideClass));
    }

    showItemsByVariantId(id) {
        this.product.variants[id]['items'].forEach(item => item.classList.remove(this.itemHideClass));
    }

    showTypeItemByVariantId(type, id) {
        if (
            this.product.variants?.[id]?.[type]
            && this.product.variants[id][type].length !== 0
        ) {
            this.product.variants[id][type][0].classList.remove(this.itemHideClass);
        }
    }

    showAllPriceFromElements() {
        this.product?.['priceFromElements']?.forEach(item => item.classList.remove(this.elementHideClass));
    }

    setVariantLinkToProductLink(id) {
        if (this.product.variants[id]['modelLinkElement'].length !== 0) {
            this.product.variants[id]['modelLinkElement'][0].href = this.product.modelLink;

            if (this.preselectedColorValue !== null && this.preselectedColor !== '') {
                const link = `${this.product.modelLink}#selected_color_${this.preselectedColor}`;
                this.product.variants[id]['modelLinkElement'][0].href = link;
            }
        }
    }

    resetAllVariantLinks() {
        this.product.variantIds.forEach((variantId) => {
            const modelLinkElement = this.product.variants[variantId]['modelLinkElement'][0];

            if (modelLinkElement) {
                const modelLink = this.product.variants[variantId].modelLink;
                this.product.variants[variantId]['modelLinkElement'][0].href = modelLink || this.product.modelLink;
            }
        });
    }

    setSelectedVariantSelectOptionByIndex(selectedIndex) {
        this.variantselect.options[this.selectedOptionIndex].removeAttribute('selected');
        this.variantselect.options[selectedIndex].setAttribute('selected', '');
        this.selectedOptionIndex = selectedIndex;
    }

    updateProductByVariantId(id) {
        FxxlCompare.sendRequest({
            id: this.productId,
            varId: id,
            action: FXXL_COMPARE_REQUEST_ACTION_UPDATE_ITEM,
            toast: false
        }, (response) => {
            if ('updatedSharedLink' in response) FxxlCompare.updateCompareShareLink(response.updatedSharedLink);
        });
    }

    hasVariantBeenSelected() {
        return this.variantselect.value !== '' && this.variantselect.value !== null;
    }

}
